import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, Skeleton, cn } from '@hrms/ui';

export interface HomeCardProps {
  title: ReactNode;
  /** Small count/label pill beside the title, e.g. the pending-leave count. */
  tag?: ReactNode;
  /** "View all →" style link, top right. */
  viewAll?: { to: string; label?: string };
  /** Anything else for the header's right side (e.g. a shift chip). Replaces `viewAll`. */
  action?: ReactNode;
  /** Optional query state: renders skeleton / error / empty in place of `children`. */
  query?: { isPending: boolean; isError: boolean };
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  errorTitle?: string;
  skeletonHeight?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * The flat surface card every home-screen section sits in. Deliberately not `Panel`: the dashboard
 * design has no coloured top stripe and no hover lift — these cards are not interactive, only their
 * rows and links are.
 */
export function HomeCard({
  title,
  tag,
  viewAll,
  action,
  query,
  isEmpty,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  errorTitle = 'Could not load this section',
  skeletonHeight = 'h-40',
  className,
  children,
}: HomeCardProps) {
  let body: ReactNode = children;
  if (query?.isPending) body = <Skeleton className={cn('w-full', skeletonHeight)} />;
  else if (query?.isError)
    body = <EmptyState title={errorTitle} description="Please try again." className="py-8" />;
  else if (isEmpty)
    body = <EmptyState title={emptyTitle} description={emptyDescription} className="px-2 py-8" />;

  return (
    <section
      className={cn(
        'flex min-w-0 flex-col rounded-2xl border border-border bg-surface p-5 shadow-xs',
        className,
      )}
    >
      <header className="mb-4 flex items-center gap-2">
        <h2 className="font-display text-sm font-extrabold text-fg">{title}</h2>
        {tag && <span className="rounded-full px-1.5 py-0.5 text-[11px] font-semibold">{tag}</span>}
        <span className="ml-auto flex items-center">
          {action ??
            (viewAll && (
              <Link
                to={viewAll.to}
                className="text-xs font-semibold text-accent hover:underline flex items-center gap-0.5"
              >
                {viewAll.label ?? 'View all'} <span className="text-[10px]">→</span>
              </Link>
            ))}
        </span>
      </header>
      {body}
    </section>
  );
}
